<script>
  // @ts-check
  import Grid from "../atoms/Grid.svelte";
  import GridContainer from "../atoms/GridContainer.svelte";
  import Image from "../atoms/Image.svelte";
  import Link from "../atoms/Link.svelte";
  import Button from "../atoms/Button.svelte";
  import DynamicHeading from "../atoms/DynamicHeading.svelte";
  import DynamicHeadingContext from "../atoms/DynamicHeadingContext.svelte";

  /**
   * @typedef {Object} FeaturedStory
   * @property {string} title
   * @property {string} subtitle
   * @property {import('../types/shared.js').Image} image
   * @property {import('../types/shared.js').CTA} cta
   */

  /**
   * Primarily used for testing
   * @type {string}
   */
  export let id = undefined;

  /**
   * Additional classes which apply to the container
   * @type { string }
   * */
  let className = "";
  export { className as class };

  /**
   * The title of the hero section
   * @type {string}
   */
  export let title;

  /**
   * The subtitle of the hero section
   * @type {string}
   */
  export let subtitle = null;

  /**
   * The call to action for the component
   * @type {import('../types/shared.js').CTA} CTA
   */
  export let cta = null;

  /**
   * The featured image in the hero section
   * @type {import('../types/shared.js').Image} Image
   */
  export let featuredImage = null;

  /**
   * The optional featured story section
   * @type {FeaturedStory}
   */
  export let featuredStory = null;

  const pseudoElementClass =
    "relative lg:static before:content-[''] before:absolute before:h-[1px] before:w-full before:top-0 lg:before:h-full lg:before:w-[1px] before:bg-black before:left-1/2 before:-translate-x-1/2";

  const columnClass = featuredStory ? "lg:col-span-6" : null;

  const subtitleClass = featuredStory
    ? "body-md mt-4 mb-7.5 lg:mt-6 lg:mb-0"
    : "body-lg mt-4 lg:mt-6 xl:mt-8";

  const isTextOnly = !featuredImage && !featuredStory && !cta && !subtitle;
  const isFeatureImageLast = !featuredStory && featuredImage;

  const getContainerPaddingBottom = () => {
    if (isTextOnly) {
      return "pb-10 md:pb-12.5";
    }
    if (isFeatureImageLast) {
      return "pb-15 md:pb-20 lg:pb-25";
    }
    return "pb-7.5 lg:pb-15";
  };
</script>

<GridContainer
  {id}
  containerElement="section"
  containerClass="pt-15 md:pt-20 xl:pt-25 {getContainerPaddingBottom()}"
>
  <div class={className}>
    <Grid gridClass="lg:relative">
      <div class="col-full-width {columnClass}">
        <!-- Since Heroes are generally H1, we don't wrap with DynamicHeadingContext 
          to bump the heading level -->
        <DynamicHeading class="heading-xl {featuredStory ? 'lg:heading-lg' : ''}"
          >{title}</DynamicHeading
        >
        {#if subtitle}
          <p class={subtitleClass}>{subtitle}</p>
        {/if}
        {#if cta}
          <Button
            url={cta.url}
            variant="outline"
            label={cta.text}
            ariaLabel={cta.text}
            buttonClass="w-fit mt-4 lg:mt-6 xl:mt-7.5"
          />
        {/if}
        {#if featuredImage && !featuredStory}
          <Image
            {...featuredImage}
            aspectRatioClasses="aspect-3/2"
            containerClass="pt-7.5 lg:pt-12.5"
          />
        {/if}
      </div>
      {#if featuredStory}
        <DynamicHeadingContext>
          <div class="col-full-width pt-7.5 lg:pt-0 {columnClass} {pseudoElementClass}">
            <Image {...featuredStory.image} aspectRatioClasses="aspect-1/1" />
            <DynamicHeading class="heading-xs pt-5 xl:pt-7">{featuredStory.title}</DynamicHeading>
            <p class="body-sm pt-3">{featuredStory.subtitle}</p>
            <Link
              href={featuredStory.cta.url}
              label={featuredStory.cta.text}
              variant="small"
              theme="white"
              class="pt-7 xl:pt-9"
            />
          </div>
        </DynamicHeadingContext>
      {/if}
    </Grid>
  </div>
</GridContainer>
